import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PHONE_STATE } from './phone-state';
import { UserService } from 'app/services/user/user.service';
import { Subscription } from 'rxjs';
import { CallingCodeOption } from 'app/model/constant';
import { CallingCode } from 'app/model/user/calling-code-model';
import { Option } from 'app/model/dropdown/dropdown-option-model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PhoneNumberValidator, transformPhoneNumber } from 'app/utilities/phone-number-validator';
import { MdDialog, MdDialogRef } from '@angular/material';
import { AuthenticationService } from 'app/services/authentication/authentication.service';
import { ChangePhoneRequest, RequiredVerifyUserResponse } from 'app/model/authen/authen';
import { OTPPurpose, VerificationMethodModel, VerifyOTPResponse } from 'app/model/otp/otp.model';
import { TranslatePipe } from 'app/pipes/translate.pipe';
import { DialogErrorComponent } from '../dialog-error/dialog-error.component';
import { DialogAlertComponent } from '../dialog-alert/dialog-alert.component';
import { VerifyOtpComponent } from 'app/components/verify-otp/verify-otp.component';

@Component({
  selector: 'act-dialog-phone',
  templateUrl: './dialog-phone.component.html',
  styleUrls: ['./dialog-phone.component.scss']
})
export class DialogPhoneComponent implements OnInit, OnDestroy {
  @ViewChild('otpVerifyChild') otpVerifyChild: VerifyOtpComponent;

  public elementId = 'dialog-phone';
  public isLoading = false;
  public backButtonMode?: 'back' | 'cancel' = 'cancel';
  public state: PHONE_STATE = PHONE_STATE.SET_PHONE;
  public PHONE_STATE = PHONE_STATE;
  public OTP_PURPOSE = OTPPurpose;
  public callingCodeList: Option[] = [];
  public defaultOption = CallingCodeOption.DEFAULT;
  public phoneNumberMask = Array.from({ length: 20 }, () => /\d/);
  public form: FormGroup;
  public otpToken: string;
  public otpMethods: VerificationMethodModel[] = [];

  private accountLocked = false;
  private callingCodeSubscribe: Subscription;

  get otpInputForm() {
    if (this.otpVerifyChild) {
      return this.otpVerifyChild.otpInputForm
    }
    return undefined;
  }

  constructor(
    private dialogRef: MdDialogRef<DialogPhoneComponent>,
    private formBuilder: FormBuilder,
    private translatePipe: TranslatePipe,
    private dialog: MdDialog,
    private userService: UserService,
    private authenticationService: AuthenticationService,
  ) {
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      mobileNumber: ['', [Validators.required, PhoneNumberValidator(CallingCodeOption.DEFAULT.value)]],
      countryISO2: [this.defaultOption.value],
    });
    this.callingCodeSubscribe = this.userService.getCallingCode().subscribe(res => {
      this.transformCallingCode(res);
    });
  }

  ngOnDestroy() {
    this.callingCodeSubscribe && this.callingCodeSubscribe.unsubscribe();
  }

  onClickBack() {
    if (this.accountLocked) {
      this.userService.logout();
      return;
    }
    if (this.state == PHONE_STATE.OTP) {
      this.state = PHONE_STATE.METHODS;
      this.backButtonMode = undefined;
    }
  }

  onClickCancel() {
    if (this.accountLocked) {
      this.dialogRef.afterClosed().take(1).subscribe(() => {
        this.userService.logout();
      });
    }
    this.dialogRef.close();
  }

  onSubmit() {
    if (this.state == PHONE_STATE.SET_PHONE) {
      this.setPhone();
    } else if (this.state == PHONE_STATE.METHODS) {
      this.onClickCancel()
    } else if (this.state == PHONE_STATE.OTP) {
      this.isLoading = true;
      this.otpVerifyChild.onSubmit();
    }
  }

  setPhone(verifiedToken?: string) {
    this.isLoading = true;
    const request: ChangePhoneRequest = {
      countryCallingCode: this.callingCodeList.find(c => c.value == this.form.value.countryISO2).displayValue,
      countryISO2: this.form.value.countryISO2,
      phone: this.form.value.mobileNumber,
      verifiedToken: verifiedToken,
    }
    this.authenticationService.changePhoneNumber(request).subscribe(
      (res) => {
        switch (res.status) {
          case 'success':
            this.dialogRef.afterClosed().take(1).subscribe(() => {
              this.showDialogSuccess();
            });
            this.dialogRef.close({
              telephone: transformPhoneNumber(request.phone, request.countryISO2),
              countryCallingCode: request.countryCallingCode
            });
            break;
          case 'require_verification':
            this.requireVerifyOtp(res.verification);
            break;
        }
        this.isLoading = false;
      },
      (err) => {
        this.dialogRef.afterClosed().take(1).subscribe(() => {
          this.showDialogError(this.translatePipe.transform('system-error'))
        })
        this.dialogRef.close();
      });
  }

  requireVerifyOtp(verification: RequiredVerifyUserResponse) {
    this.otpToken = verification.token;
    this.otpMethods = verification.methods;
    this.state = PHONE_STATE.METHODS;
    this.backButtonMode = undefined;
  }

  transformCallingCode(list: CallingCode[]) {
    this.callingCodeList = [];
    list.forEach(item => {
      if (this.defaultOption.value === item.countryISO2) {
        this.callingCodeList.unshift(new Option(
          item.countryNameEN + ' (' + item.countryCallingCode + ')',
          item.countryISO2,
          item.countryISO2 ? 'flags/' + item.countryISO2.toLowerCase() + '.png' : '',
          item.countryCallingCode
        ));
      } else {
        this.callingCodeList.push(new Option(
          item.countryNameEN + ' (' + item.countryCallingCode + ')',
          item.countryISO2,
          item.countryISO2 ? 'flags/' + item.countryISO2.toLowerCase() + '.png' : '',
          item.countryCallingCode
        ));
      }
    });
  }

  onSelectCallingCode(countryISO2: string) {
    this.form.controls.mobileNumber.setValidators([Validators.required, PhoneNumberValidator(countryISO2)]);
    this.form.controls.mobileNumber.updateValueAndValidity();
    if (this.form.controls.mobileNumber.value) {
      this.form.controls.mobileNumber.markAsTouched();
    }
  }

  isInvalid(field: string) {
    return this.form.get(field).invalid && this.form.get(field).touched;
  }

  onVerifiedOtp(event: VerifyOTPResponse) {
    this.setPhone(event.verifiedToken);
  }

  onOtpStateChange(event: PHONE_STATE) {
    this.state = event;
    if (event == PHONE_STATE.METHODS) {
      this.backButtonMode = undefined;
    } else if (event == PHONE_STATE.OTP) {
      this.backButtonMode = 'back';
    } else if (event == PHONE_STATE.SESSION_EXPIRE) {
      this.backButtonMode = 'cancel';
    }
  }

  onAccountLocked() {
    this.accountLocked = true;
  }

  showDialogError(message: string, subText?: string) {
    const dialogRef: MdDialogRef<DialogErrorComponent> = this.dialog.open(DialogErrorComponent);
    dialogRef.componentInstance.text = message;
    if (subText) {
      dialogRef.componentInstance.subText = subText;
    }
  }

  showDialogSuccess() {
    const successPopupRef = this.dialog.open(DialogAlertComponent)
    successPopupRef.componentInstance.imageSrc = '/assets/images/icons/icon-setup-pin-success.png'
    successPopupRef.componentInstance.alertHeader = this.translatePipe.transform('change-phone-success');
    successPopupRef.componentInstance.autoClose = 10;
  }

}



// WEBPACK FOOTER //
// ./src/app/components/modal/dialog-phone/dialog-phone.component.ts